/*
 * day.js — this device's record of today's work session (FD-001 lifecycle:
 * working → checked-out → closed). Scoped to a single day on a single device;
 * cleared implicitly by the date rolling over. This is NOT persistent
 * authentication (deferred) — just the state FD-001 itself requires so a
 * check-out can happen after the app was closed and reopened.
 */
import { REPORTING_WINDOW_MINUTES } from '../../shared/captureMapping.js'

const KEY = 'flow.day.v1'

function todayStamp() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Reporting window in ms. Test override: localStorage 'flow.reportingWindowMs'. */
export function reportingWindowMs() {
  try {
    const o = Number(localStorage.getItem('flow.reportingWindowMs'))
    if (Number.isFinite(o) && o > 0) return o
  } catch { /* noop */ }
  return REPORTING_WINDOW_MINUTES * 60_000
}

/**
 * @returns today's day record or null:
 * { date, status: 'working'|'checked-out'|'closed',
 *   captureId, recordId|null, held,
 *   checkedInAt, checkedOutAt|null,
 *   session: { tenant, employee, project, confidence } }
 */
export function loadDay() {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const rec = JSON.parse(raw)
    if (rec?.date !== todayStamp()) return null // stale day — ignored
    if (!rec?.session?.employee?.name) return null
    return rec
  } catch {
    return null
  }
}

export function startDay({ captureId, recordId, held, checkedInAt, session }) {
  const rec = {
    date: todayStamp(),
    status: 'working',
    captureId,
    recordId: recordId ?? null,
    held: !!held,
    checkedInAt,
    checkedOutAt: null,
    session: {
      tenant: session.tenant,
      employee: session.employee,
      project: session.project,
      confidence: session.confidence,
    },
  }
  persist(rec)
  return rec
}

export function updateDay(patch) {
  const rec = loadDay()
  if (!rec) return null
  const next = { ...rec, ...patch }
  persist(next)
  return next
}

/** Called when a queued check-in syncs and the real recordId becomes known. */
export function linkRecordId(captureId, recordId) {
  const rec = loadDay()
  if (rec && rec.captureId === captureId && !rec.recordId) {
    persist({ ...rec, recordId })
  }
}

/** When the reporting window ends for a checked-out day (null if n/a). */
export function closeAt(rec) {
  if (!rec?.checkedOutAt) return null
  return new Date(rec.checkedOutAt).getTime() + reportingWindowMs()
}

function persist(rec) {
  try { localStorage.setItem(KEY, JSON.stringify(rec)) } catch { /* private mode */ }
}
